import React from 'react'
import { hot } from 'react-hot-loader/root'
import { setConfig } from 'react-hot-loader'
import { BrowserRouter, Switch, Route, NavLink } from 'react-router-dom'
import { Col, Nav, Navbar, Row, Image, Spinner } from 'react-bootstrap'
import { DemoUseFieldArray } from 'components/DemoUseFieldArray'
import { DemoFormProvider } from 'components/DemoFormProvider'
import { DemoUseWatch } from 'components/DemoUseWatch'
import { GhgCalculator } from 'components/GhgCalculator'
import { ErrorBoundary as SentryErrorBoundary } from '@sentry/react'

import './index.scss'
import logo from 'assets/images/logo.png'
import { AuthManager } from 'components/AuthManager'
import { LOGIN_URL } from 'utils/auth'
import { GhgLandingPage } from 'components/GhgLandingPage/GhgLandingPage'

setConfig({
  reloadHooks: false,
})

// if you are integrating redux this is a good place to wrap your app in <Provider store={store}>
// if you are using react-router this is a good place to set up your router
// this setup needs to be done in a separate file from index.jsx to enable hot reloads

const App: React.SFC = () => (
  // If Sentry hasn't been initialized (i.e. in dev and tests) this just acts
  // like a normal <ErrorBoundary> and doesn't try to send any data anywhere.
  <SentryErrorBoundary
    // TODO: Change this to something more user-friendly before production go-live.
    fallback={(errorDetails) => {
      console.log('Error captured by error boundary', errorDetails)
      return (
        <>
          <h5>Front end error</h5>
          <pre>{errorDetails.error.toString()}</pre>
          <pre>{errorDetails.componentStack}</pre>
        </>
      )
    }}
  >
    <BrowserRouter>
      <AuthManager>
        {({ authInfo, authFailed }) => (
          <>
            <a href="#main-content" className="sr-only">
              Skip to main content
            </a>
            <header>
              <Navbar bg="dark" variant="dark">
                <Nav>
                  <NavLink to="/" exact className="navbar-brand">
                    <Image src={logo} alt="Beef + Lamb New Zealand" className="mr-3" width="200" />
                    GHG Calculator
                  </NavLink>
                </Nav>
                {authInfo && (
                  <Navbar.Collapse className="justify-content-end">
                    <Navbar.Text>
                      Signed in as {authInfo.user.first_name}
                      {/* (
                          // TODO: Need a logout endpoint to actually clear our current session cookie.
                      <a
                        onClick={() => {
                          // ???
                        }}
                      >
                        Log out
                      </a>
                      ) */}
                    </Navbar.Text>
                  </Navbar.Collapse>
                )}
              </Navbar>
            </header>
            <main id="main-content" className="container-xl main-content">
              <Row>
                <Col>
                  {authInfo ? (
                    <Switch>
                      <Route path="/demo/use-field-array" component={DemoUseFieldArray} />
                      <Route path="/demo/form-provider" component={DemoFormProvider} />
                      <Route path="/demo/use-watch" component={DemoUseWatch} />
                      <Route path="/new" render={() => <GhgCalculator user={authInfo.user} />} />
                      <Route
                        path="/edit/:id"
                        render={() => <GhgCalculator user={authInfo.user} />}
                      />
                      <Route path="/" render={() => <GhgLandingPage user={authInfo.user} />} />
                    </Switch>
                  ) : (
                    <>
                      <div className="row justify-content-center bg-light mb-5">
                        <div className="col-10">
                          <h1 className="text-center mt-5">Greenhouse Gas Emissions Calculation</h1>
                        </div>
                      </div>
                      {authFailed ? (
                        <div className="text-center">
                          Your session has expired. To continue, please log in to{' '}
                          <a href={LOGIN_URL}>Beef and Lamb New Zealand</a> and click the "Go to
                          Yardstick" button on your user dashboard.
                        </div>
                      ) : (
                        <div className="text-center">
                          <Spinner animation="border" role="status">
                            <span className="sr-only">Authenticating...</span>
                          </Spinner>
                        </div>
                      )}
                    </>
                  )}
                </Col>
              </Row>
            </main>
            <footer className="container-fluid site-footer text-center pt-4 pb-2">
              <Row>
                <Col>
                  <p>Beef + Lamb New Zealand</p>
                </Col>
              </Row>
            </footer>
          </>
        )}
      </AuthManager>
    </BrowserRouter>
  </SentryErrorBoundary>
)

// eslint-disable-next-line import/no-default-export
export default hot(App)
